/**
 * Whether the member has agreed to analytics.
 *
 * WHY THIS EXISTS. PostHog was initialising on load, in production, with
 * `persistence: "localStorage"`, and nothing had ever asked. PECR wants consent
 * BEFORE anything non-essential is stored on someone's device, and an analytics
 * SDK stores something the moment it starts. Found on 2026-08-12 by the free
 * website audit we had just built, which is a satisfying way to find it and an
 * uncomfortable one.
 *
 * ONE KEY, NOT TWO. `components/analytics-consent.tsx` already gated Vercel
 * Analytics and Speed Insights behind `growthsync-analytics-consent`, defaulting
 * to off, with a toggle in Settings. That part was right all along; PostHog
 * simply never asked it. So this reads and writes THE SAME KEY rather than
 * introducing a second one, and the Settings toggle now governs everything.
 * Two consent keys is how a member turns analytics off and stays tracked.
 *
 * Deliberately not a React store: this is read during app start, before React
 * has mounted anything, so it has to work as a plain function.
 */

const KEY = "growthsync-analytics-consent";

export type ConsentChoice = "granted" | "denied";

/** Null means they have not been asked yet, which is not the same as a no. */
export function readConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === "true") return "granted";
    if (v === "false") return "denied";
    return null;
  } catch {
    /* Private mode, or storage disabled. Treat as not asked and store nothing. */
    return null;
  }
}

export function writeConsent(choice: ConsentChoice): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, choice === "granted" ? "true" : "false");
  } catch {
    /* If we cannot remember the answer we will ask again next time, which is
       the safe direction to fail in. */
  }
}

export function hasAnalyticsConsent(): boolean {
  return readConsent() === "granted";
}
